import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Share2, Lock } from "lucide-react";

const API = "/api";

/**
 * Reusable Referral Card component
 * Props:
 *   - data: { code, is_paying, reward_credits, referrer_name }
 *   - compact: bool — smaller version for dashboard sidebar
 *   - getAuthHeader: function
 */
export default function ReferralCard({ data, compact = false, getAuthHeader }) {
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  const shareUrl = data.code
    ? `${window.location.origin}/book-trial?ref=${data.code}`
    : "";
  const shareText = data.code
    ? `Hey! I'm learning English at LanguaConnect with amazing tutors. Use my code ${data.code} for a free trial: ${shareUrl}`
    : "";

  const copyCode = () => {
    if (!data.code) return;
    navigator.clipboard.writeText(data.code);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied!");
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  // Locked state — non-paying student
  if (!data.is_paying) {
    return (
      <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50" data-testid="referral-card-locked">
        <CardContent className={compact ? "p-4" : "p-6"}>
          <div className="flex items-start gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-900">Refer & Earn — Unlock Soon!</h3>
              <p className="text-sm text-amber-800 mt-1">
                Subscribe to your first paid package to unlock your referral code.
                Earn <strong>{data.reward_credits} paid class credits</strong> for each friend who subscribes!
              </p>
              <a
                href="/subscribe"
                className="inline-block mt-3 text-sm font-semibold text-amber-700 underline hover:text-amber-900"
                data-testid="unlock-referral-link"
              >
                Browse subscription packages →
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-0 shadow-lg overflow-hidden"
      style={{ background: "linear-gradient(135deg, #5FE3D0 0%, #1E3A8A 100%)" }}
      data-testid="referral-card"
    >
      <CardContent className={compact ? "p-4" : "p-6"}>
        <div className="text-white">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5" />
            <h3 className="font-bold text-lg">Refer a Friend</h3>
          </div>
          <p className="text-sm text-white/90">
            Earn <strong>+{data.reward_credits} paid class credits</strong> when your friend subscribes to any package!
          </p>

          <div className="mt-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg p-3 flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/70">Your code</p>
              <p className="font-mono font-bold text-xl tracking-wide" data-testid="referral-code">{data.code}</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={copyCode}
              className="bg-white text-slate-900 hover:bg-slate-100"
              data-testid="copy-referral-code-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> {copied ? "Copied!" : "Copy"}
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={shareWhatsApp}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-3.5 h-3.5 mr-1" /> WhatsApp
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={copyLink}
              className="bg-white/10 border-white/40 text-white hover:bg-white/20"
              data-testid="copy-share-link-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy Share Link
            </Button>
            {!compact && (
              <a
                href="/refer"
                className="inline-flex items-center text-xs text-white/90 underline ml-auto self-center"
                data-testid="view-referrals-link"
              >
                View history →
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
